'use client';

/**
 * @fileoverview `useNotificationPanel` — open/close state for the global
 * NotificationCenter surface (WEB-635).
 *
 * Mirrors `useSearchSheet`: a module-scoped `useSyncExternalStore` so the
 * nav-menu Notifications row can open the panel after the menu sheet closes
 * without prop-drilling or a second provider. The AppHeader bell is retired;
 * this store is the sole activation path into `NotificationCenter`.
 *
 * Count data stays on `useGetUnreadCountQuery` — this hook only owns open state.
 */

import * as React from 'react';

interface NotificationPanelState {
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly openPanel: () => void;
  readonly closePanel: () => void;
}

type Listener = () => void;

let currentOpen = false;
const listeners = new Set<Listener>();

function notifyListeners(): void {
  for (const listener of [...listeners]) listener();
}

function setOpenImpl(next: boolean): void {
  if (currentOpen === next) return;
  currentOpen = next;
  notifyListeners();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return currentOpen;
}

/** SSR always closed — stable identity for hydration. */
function getServerSnapshot(): boolean {
  return false;
}

/**
 * React hook for the global NotificationCenter open/close state.
 *
 * @example
 * ```tsx
 * // Menu row (WEB-635)
 * const { openPanel } = useNotificationPanel();
 * <button onClick={() => openPanel()}>Notifications</button>
 *
 * // NotificationCenter mount
 * const { open, setOpen } = useNotificationPanel();
 * <Sheet open={open} onOpenChange={setOpen}>…</Sheet>
 * ```
 */
export function useNotificationPanel(): NotificationPanelState {
  const open = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setOpen = React.useCallback((next: boolean) => {
    setOpenImpl(next);
  }, []);
  const openPanel = React.useCallback(() => {
    setOpenImpl(true);
  }, []);
  const closePanel = React.useCallback(() => {
    setOpenImpl(false);
  }, []);

  return { open, setOpen, openPanel, closePanel };
}

/**
 * Reset the store between tests. **Do not call from production code.**
 * @internal
 */
export function __resetNotificationPanelStateForTests(): void {
  setOpenImpl(false);
}
